import { store } from '@redux/store';
import uiStateSlice from 'layout/redux/uiStateSlice';
import { fromEvent, filter, map, tap } from 'rxjs';
import openLeadModal from 'features/searchResultsExtendedCard';
import housingCardConstructor from '../services/getHousingCard';

require('features/leadGenerators');
require('../analytics/leads');

/**
 * Root node containing all search result items.
 * Used as the delegated click event source.
 */
const searchResultsContainer = document.querySelector(
  '[data-search-results-content]'
);

/**
 * Stream: all click events inside the search results container.
 */
const containerClick$ = fromEvent(searchResultsContainer, 'click');

/**
 * Returns true if the event originated within a Summary Card.
 * @param {MouseEvent} event
 * @returns {boolean}
 */
const isSummaryCard = event => Boolean(event.target.closest('[data-map-summary-card]'));

/**
 * Returns true if the event target is an HTMLAnchorElement.
 * @param {MouseEvent} event
 * @returns {boolean}
 */
const isAnchorElement = event => event.target instanceof HTMLAnchorElement;

/**
 * Returns true if the click originated from a Lead Generator opener.
 * @param {MouseEvent} event
 * @returns {boolean}
 */
const isLeadOpener = event => Boolean(event.target.closest('[data-lead-generator-opener]'));

/**
 * Returns true if the user clicked on the Interactive Site Plan control.
 * @param {MouseEvent} event
 * @returns {boolean}
 */
const isInteractiveSitePlan = event => Boolean(event.target.closest('[data-interactive-plan-button]'));

/**
 * Stream: represents valid Summary Card clicks that should
 * trigger navigation and analytics tracking.
 */
const cardClick$ = containerClick$.pipe(
  filter(isSummaryCard),
  filter(event => !isAnchorElement(event)),
  filter(event => !isLeadOpener(event)),
  filter(event => !isInteractiveSitePlan(event)),
  tap((event) => event.preventDefault()),
  map(event => event.target.closest('[data-map-summary-card]')),
  filter(Boolean)
);

/**
 * Stream: represents clicks on Lead Generator openers.
 * Maps the event to the corresponding Summary Card element.
 */
const leadOpenerClick$ = containerClick$.pipe(
  filter(isLeadOpener),
  map(event => event.target.closest('[data-map-summary-card]')),
  filter(Boolean)
);

/**
 * Opens the provided URL in a new browser tab.
 * @param {string} url
 */
const redirectToUrl = url => {
  if (!url) {
    return;
  }
  window.open(url, '_blank');
};

cardClick$.subscribe(summaryCard => {
  const cardDataset = summaryCard.dataset;

  const url =
    cardDataset?.url ||
    summaryCard.querySelector('a')?.href;

  const googleAnalyticsDTO = housingCardConstructor(
    cardDataset.type,
    url,
    cardDataset
  ).getCardGoogleAnalyticsEvent();

  store.dispatch(
    uiStateSlice.actions.trackUserEvent({ googleAnalyticsDTO })
  );

  redirectToUrl(url);
});

leadOpenerClick$.subscribe(leadOpener => {
  openLeadModal(
    leadOpener.dataset.id,
    leadOpener.dataset.type
  );
});
